import { CSSProperties, ReactElement, ReactNode, cloneElement, isValidElement } from 'react';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { FormError } from '@components/form-error';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconChevronDownSmall } from '@components/icons/chevron-down-small';
import { IconTrash } from '@components/icons/trash';
import { IconUndo } from '@components/icons/undo';
import { Menu } from '@components/menu';
import { showModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import {
  FIELD_MAPPING_ICON_COLOR,
  fieldTypeIconMap,
} from '@components/text-editors/addons/field-mapping-type-maps';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AddFieldsModal, FIELD_TYPE_BY_ATTRIBUTE_TYPE } from './add-fields-modal';
import {
  addFieldsToDraft,
  draftKey,
  mergeDraftFieldUpdate,
  removeFieldFromDraft,
} from './serialize';
import {
  FieldConfigDraft,
  FieldConfigValue,
  JourneyAttribute,
  JourneyAttributeType,
} from './types';
import { ValueInput } from './value-input';

/**
 * An always-required base mapping row (Full name / Email) rendered above the
 * added attribute fields. Only the global config sidebar passes these — the
 * per-job panel edits attribute fields only.
 */
export type BaseFieldRow = {
  key: string;
  label: string;
  attributeType: JourneyAttributeType;
  editor: ReactNode;
  /** Validation message shown under the editor — set when the mapping is empty. */
  error?: string;
};

type FieldConfigFormProps = {
  attrs: JourneyAttribute[];
  draft: FieldConfigDraft;
  variableGroups: FieldMappingGroup[];
  fieldMappingId: string;
  onChange: (draft: FieldConfigDraft) => void;
  baseRows?: BaseFieldRow[];
  fileVariableGroups: FieldMappingGroup[];
  fileMultiVariableGroups: FieldMappingGroup[];
  /** Short ids whose per-job state diverges from the global config — badged "Overridden". Per-job panel only. */
  overriddenShortIds?: ReadonlySet<string>;
  /** Hands a field back to global inheritance. When set, overridden rows get a reset button. Per-job panel only. */
  onResetField?: (shortId: string) => void;
  /** Per-shortId generation counters. Bumping one remounts that field's row so
   *  uncontrolled editors (text/paragraph/email/phone) re-read their content —
   *  the reset-to-global path rewrites the draft value underneath them. */
  remountKeys?: Record<string, number>;
};

// Shared id prefix for the base-field validation alert. Suffixed per
// row (`-${row.key}`) rather than reused bare, since the base rows (Full
// name / Email / Phone) each carry their own independent error and can be
// invalid simultaneously — a single shared id across rows would duplicate
// the id attribute in the DOM and make aria-describedby ambiguous.
const BASE_FIELD_ERROR_ID = 'base-field-config-error';

const FieldTypeIcon = ({ attributeType }: { attributeType: JourneyAttributeType }) => {
  const TypeIcon = fieldTypeIconMap[FIELD_TYPE_BY_ATTRIBUTE_TYPE[attributeType] ?? 'text'];
  return <TypeIcon size={16} color={FIELD_MAPPING_ICON_COLOR} />;
};

/**
 * The field configuration form shared by the global config sidebar and the
 * per-job trigger config sidebar.
 *
 * Flat list — no sections or collapsibles:
 * - Base rows (Full name / Email): type icon + label + green "Always
 *   required" badge, mapping editor below. Not removable.
 * - Each added field (any journey attribute the admin opted in): type icon +
 *   title + small Required/Optional selector (the Hire-side "marked required"
 *   flag) + trash button, value input below.
 * - A full-width "+ Add fields" button opening the add-fields modal with
 *   every attribute not yet added.
 */
export const FieldConfigForm = ({
  attrs,
  draft,
  variableGroups,
  fieldMappingId,
  onChange,
  baseRows = [],
  fileVariableGroups,
  fileMultiVariableGroups,
  overriddenShortIds,
  onResetField,
  remountKeys,
}: FieldConfigFormProps) => {
  const { t } = useScopedTranslate(locale);

  // Added fields are the draft entries opted in (any attribute — the panel
  // makes no required/optional distinction beyond the toggleable flag).
  // Keyed by draftKey, not shortId: a journey attribute and a company-traveller
  // attribute can share a short_id, and the draft keeps them apart.
  const includedAttrs = attrs.filter((attr) => draft.fields[draftKey(attr)]?.included);
  // A field disabled in Journeys stays visible while added (so it can be
  // removed, mirroring the stale-field grace) but is not offered for adding —
  // the send sidebar already refuses to send it. `!== false` rather than a
  // truthy check: the traveller catalog relays upstream data verbatim, and a
  // missing flag must not hide the whole catalog.
  const availableToAdd = attrs.filter(
    (attr) => attr.isActive !== false && !draft.fields[draftKey(attr)]?.included,
  );

  const handleValueChange = (shortId: string, value: FieldConfigValue) => {
    onChange(mergeDraftFieldUpdate(draft, shortId, { value }));
  };

  const handleRequiredChange = (shortId: string, isRequired: boolean) => {
    onChange(mergeDraftFieldUpdate(draft, shortId, { isRequired }));
  };

  const handleAddFields = (shortIds: string[]) => {
    onChange(addFieldsToDraft(draft, shortIds, attrs));
  };

  const handleRemoveField = (shortId: string) => {
    onChange(removeFieldFromDraft(draft, shortId));
  };

  const openAddModal = () => {
    showModal({
      component: AddFieldsModal,
      props: {
        availableFields: availableToAdd,
        onAdd: handleAddFields,
      },
    });
  };

  if (baseRows.length === 0 && attrs.length === 0) {
    return <Text font="regular14" color="textHelper" text={t('noFields')} />;
  }

  return (
    <Stack space={3}>
      {baseRows.map((row) => {
        const hasError = Boolean(row.error);
        const errorId = `${BASE_FIELD_ERROR_ID}-${row.key}`;
        // Links the editor to its error the same way a native input/label
        // pair would: aria-describedby only while the error is
        // present, pointing at the role="alert" container below so
        // assistive tech both announces the message and lets the user find
        // it again from the field itself. row.editor is built by the caller
        // (an opaque ReactNode), so cloning is the only local way to attach
        // the id without reaching into ui/core's editor components.
        const editor =
          hasError && isValidElement(row.editor)
            ? cloneElement(row.editor as ReactElement<Record<string, unknown>>, {
                'aria-describedby': errorId,
              })
            : row.editor;
        return (
          <FieldRow key={row.key} data-testid={`journey-field:${row.label}`}>
            <Stack space={1}>
              <Stack orientation="horizontal" align="center" space={1}>
                <FieldTypeIcon attributeType={row.attributeType} />
                <TitleBlock>
                  <Text font="semibold14" text={row.label} ellipsisWithTooltip />
                </TitleBlock>
                <Badge variant="green" text={t('alwaysRequired')} />
              </Stack>
              {editor}
              {hasError && (
                <div role="alert" id={errorId}>
                  <FormError text={row.error} />
                </div>
              )}
            </Stack>
          </FieldRow>
        );
      })}

      {includedAttrs.map((attr) => {
        const key = draftKey(attr);
        const entry = draft.fields[key];
        const requiredColor = entry.isRequired ? 'elementGreen200' : 'elementOrange200';
        const isOverridden = overriddenShortIds?.has(key) ?? false;
        return (
          <FieldRow
            key={`${key}:${remountKeys?.[key] ?? 0}`}
            data-testid={`journey-field:${attr.title}`}
          >
            <Stack space={1}>
              <Stack orientation="horizontal" align="center" space={1}>
                <FieldTypeIcon attributeType={attr.attributeType} />
                <TitleBlock>
                  <Text font="semibold14" text={attr.title} ellipsisWithTooltip />
                </TitleBlock>
                {isOverridden && <Badge variant="blue" text={t('overridden')} />}
                <Menu
                  placement="bottom-end"
                  buttonProps={{
                    variant: 'custom',
                    size: 'sm',
                    font: 'semibold12',
                    color: requiredColor,
                    text: entry.isRequired ? t('required') : t('optional'),
                    iconRight: <IconChevronDownSmall size={16} color={requiredColor} />,
                    style: entry.isRequired ? requiredPillStyle : optionalPillStyle,
                    'aria-label': t('requiredSelectLabel', { title: attr.title }),
                  }}
                >
                  <Menu.Item
                    label={t('required')}
                    isActive={entry.isRequired}
                    iconRight={
                      entry.isRequired ? <IconCheckmark size={16} color="elementBlue100" /> : null
                    }
                    onSelect={() => handleRequiredChange(key, true)}
                  />
                  <Menu.Item
                    label={t('optional')}
                    isActive={!entry.isRequired}
                    iconRight={
                      entry.isRequired ? null : <IconCheckmark size={16} color="elementBlue100" />
                    }
                    onSelect={() => handleRequiredChange(key, false)}
                  />
                </Menu>
                {isOverridden && onResetField && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<IconUndo size={16} />}
                    tooltip={t('resetToGlobal')}
                    aria-label={t('resetToGlobalLabel', { title: attr.title })}
                    onClick={() => onResetField(key)}
                  />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<IconTrash size={16} />}
                  tooltip={t('removeField')}
                  aria-label={t('removeField')}
                  onClick={() => handleRemoveField(key)}
                />
              </Stack>
              <ValueInput
                shortId={key}
                title={attr.title}
                attributeType={attr.attributeType}
                options={attr.options ?? null}
                value={entry.value}
                variableGroups={variableGroups}
                fileVariableGroups={fileVariableGroups}
                fileMultiVariableGroups={fileMultiVariableGroups}
                fieldMappingId={fieldMappingId}
                onChange={handleValueChange}
              />
            </Stack>
          </FieldRow>
        );
      })}

      {availableToAdd.length > 0 && (
        <Button
          variant="primary-blue"
          isFullWidth
          iconLeft={<IconAddPlus />}
          text={t('addFields')}
          onClick={openAddModal}
        />
      )}
    </Stack>
  );
};

// The pill uses the `custom` Button variant — the preset variants have no
// green/orange tinted backgrounds (same approach as the stage journey chip).
const requiredPillStyle = {
  '--custom-button-bg-color': theme.color.greenTrans10,
  '--custom-button-bg-color-hover': theme.color.greenTrans20,
} as CSSProperties;

const optionalPillStyle = {
  '--custom-button-bg-color': theme.color.orangeTrans10,
  '--custom-button-bg-color-hover': theme.color.orangeTrans20,
} as CSSProperties;

const FieldRow = styled.div({
  paddingBottom: pxToRem(12),
  borderBottom: `${pxToRem(1)} solid ${theme.color.border10}`,
  '&:last-of-type': {
    borderBottom: 'none',
    paddingBottom: 0,
  },
});

const TitleBlock = styled.div({
  flex: 1,
  minWidth: 0,
});

const locale = {
  en: {
    alwaysRequired: 'Always required',
    required: 'Required',
    optional: 'Optional',
    requiredSelectLabel: 'Required or optional: {title}',
    addFields: 'Add fields',
    removeField: 'Remove field',
    noFields: 'This journey has no configurable fields',
    overridden: 'Overridden',
    resetToGlobal: 'Reset to global configuration',
    resetToGlobalLabel: 'Reset {title} to global configuration',
  },
  is: {
    alwaysRequired: 'Alltaf skyldureitur',
    required: 'Skyldureitur',
    optional: 'Valfrjáls reitur',
    requiredSelectLabel: 'Skyldureitur eða valfrjáls reitur: {title}',
    addFields: 'Bæta við reitum',
    removeField: 'Fjarlægja reit',
    noFields: 'Engir reitir í þessu Journey-ferli eru stillanlegir',
    overridden: 'Sérstilling',
    resetToGlobal: 'Nota almennar stillingar aftur',
    resetToGlobalLabel: 'Nota almennar stillingar aftur fyrir reitinn „{title}“',
  },
};
